/**
 * components/Users/UsersManagement.tsx - Users Management Component
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './UsersManagement.module.css';
import userService, { User } from '../../services/userService';
import toastService from '../../services/toastService';
import Modal from '../UI/Modal';
import StatTile from '../Dashboard/StatTile';
import {
  SearchIcon,
  PlusIcon,
  EditIcon,
  DeleteIcon,
  UsersIcon,
  CheckIcon,
  UserIcon,
  VendorsIcon,
} from '../UI/Icons';

type SortField = 'name' | 'email' | 'role' | 'lastLogin' | 'createdAt';

const PERMISSION_LEVELS: { value: User['role']; label: string; description: string }[] = [
  { value: 'read-only', label: 'Read Only', description: 'Can view data only' },
  { value: 'editor', label: 'Editor', description: 'Can view and edit data' },
  { value: 'preparer', label: 'Preparer', description: 'Kitchen / prep station access' },
  { value: 'deliverer', label: 'Deliverer', description: 'Delivery runner access' },
  { value: 'admin', label: 'Admin', description: 'Full access to all features' },
  { value: 'super-admin', label: 'Super Admin', description: 'System administrator' },
];

const STATUS_OPTIONS: { value: User['status']; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

interface FormErrors {
  username?: string;
  password?: string;
  name?: string;
  email?: string;
}

interface UserFormData {
  name: string;
  email: string;
  username: string;
  password: string;
  role: User['role'];
  status: User['status'];
}

interface UserFormModalProps {
  user?: User | null;
  saving?: boolean;
  onSave: (data: User | Omit<User, '_id' | 'id'>) => void;
  onClose: () => void;
}

function getUserId(user: User): string {
  return (user.id || user._id) as string;
}

function getSortKey(user: User, field: SortField): string | number {
  switch (field) {
    case 'name':
      return (user.name || '').toLowerCase();
    case 'email':
      return (user.email || '').toLowerCase();
    case 'role':
      return (user.role || '').toLowerCase();
    case 'lastLogin':
      return user.lastLogin ? new Date(user.lastLogin).getTime() : 0;
    case 'createdAt':
      return user.createdAt ? new Date(user.createdAt).getTime() : 0;
    default:
      return '';
  }
}

function getRoleBadgeClass(role: User['role'], stylesModule: typeof styles): string {
  switch (role) {
    case 'super-admin':
      return stylesModule.badgeSuperAdmin;
    case 'admin':
      return stylesModule.badgeAdmin;
    case 'editor':
      return stylesModule.badgeEditor;
    case 'preparer':
      return stylesModule.badgePreparer;
    case 'deliverer':
      return stylesModule.badgeDeliverer;
    case 'read-only':
    default:
      return stylesModule.badgeReadOnly;
  }
}

function getStatusBadgeClass(status: User['status'], stylesModule: typeof styles): string {
  switch (status) {
    case 'active':
      return stylesModule.badgeActive;
    case 'suspended':
      return stylesModule.badgeSuspended;
    case 'inactive':
    default:
      return stylesModule.badgeInactive;
  }
}

function formatDate(dateString?: string | null): string {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function UserFormModal({ user, saving = false, onSave, onClose }: UserFormModalProps) {
  const [formData, setFormData] = useState<UserFormData>({
    name: user?.name || '',
    email: user?.email || '',
    username: user?.username || '',
    password: '',
    role: user?.role || 'read-only',
    status: user?.status || 'active',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const validate = (): boolean => {
    const errors: FormErrors = {};
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!user && !formData.password.trim()) errors.password = 'Password is required for new users';
    if (!formData.name.trim()) errors.name = 'Full name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: Record<string, unknown> = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      username: formData.username.trim(),
      role: formData.role,
      status: formData.status,
    };

    if (!user) {
      payload.password = formData.password;
      onSave(payload as Omit<User, '_id' | 'id'>);
      return;
    }

    if (formData.password.trim()) {
      payload.password = formData.password;
    }

    onSave({ ...user, ...payload } as User);
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={user ? 'Edit User' : 'Create New User'}
      size="md"
      footer={
        <div className={styles.modalActions}>
          <button type="button" onClick={onClose} className={styles.modalSecondary} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form="user-form" className={styles.modalPrimary} disabled={saving}>
            {saving ? 'Saving…' : user ? 'Update User' : 'Create User'}
          </button>
        </div>
      }
    >
      <form id="user-form" onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.formLabel} htmlFor="user-username">
            Username *
          </label>
          <input
            id="user-username"
            type="text"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            className={styles.formInput}
            placeholder="Enter username (used for login)"
            autoComplete="username"
          />
          {formErrors.username && <p className={styles.fieldError}>{formErrors.username}</p>}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel} htmlFor="user-password">
            {user ? 'New Password (leave blank to keep current)' : 'Password *'}
          </label>
          <input
            id="user-password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className={styles.formInput}
            placeholder={user ? 'Leave blank to keep current password' : 'Enter password'}
            autoComplete="new-password"
          />
          {formErrors.password && <p className={styles.fieldError}>{formErrors.password}</p>}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel} htmlFor="user-name">
            Full Name *
          </label>
          <input
            id="user-name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={styles.formInput}
            placeholder="Enter full name"
          />
          {formErrors.name && <p className={styles.fieldError}>{formErrors.name}</p>}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel} htmlFor="user-email">
            Email Address *
          </label>
          <input
            id="user-email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className={styles.formInput}
            placeholder="Enter email address"
          />
          {formErrors.email && <p className={styles.fieldError}>{formErrors.email}</p>}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel} htmlFor="user-role">
            Permission Level
          </label>
          <select
            id="user-role"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
            className={styles.formSelect}
          >
            {PERMISSION_LEVELS.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label} - {level.description}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel} htmlFor="user-status">
            Status
          </label>
          <select
            id="user-status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value as User['status'] })}
            className={styles.formSelect}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </form>
    </Modal>
  );
}

interface UserCardProps {
  user: User;
  statusUpdatingId: string | null;
  onEdit: (user: User) => void;
  onDelete: (userId: string) => void;
  onToggleStatus: (user: User, nextStatus: User['status']) => void;
}

function UserCard({ user, statusUpdatingId, onEdit, onDelete, onToggleStatus }: UserCardProps) {
  const uid = getUserId(user);
  const roleLabel = PERMISSION_LEVELS.find((p) => p.value === user.role)?.label || user.role;
  const statusLabel = STATUS_OPTIONS.find((s) => s.value === user.status)?.label || user.status;
  const isUpdating = statusUpdatingId === uid;

  const handleStatusAction = () => {
    if (user.status === 'active') {
      onToggleStatus(user, 'suspended');
    } else {
      onToggleStatus(user, 'active');
    }
  };

  return (
    <div className={styles.userCard}>
      <div className={styles.userHeader}>
        <div className={styles.userInfo}>
          <h3 className={styles.userName}>{user.name}</h3>
          <p className={styles.userEmail}>{user.email}</p>
          <div className={styles.userBadges}>
            <span className={`${styles.badge} ${getRoleBadgeClass(user.role, styles)}`}>{roleLabel}</span>
            <span className={`${styles.badge} ${getStatusBadgeClass(user.status, styles)}`}>{statusLabel}</span>
          </div>
          <div className={styles.usernameChip}>Username: {user.username || user.email?.split('@')[0] || '—'}</div>
        </div>

        <div className={styles.userActions}>
          <button type="button" onClick={() => onEdit(user)} className={styles.actionButton} title="Edit User">
            <EditIcon size={16} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(uid)}
            className={`${styles.actionButton} ${styles.deleteButton}`}
            title="Delete User"
          >
            <DeleteIcon size={16} />
          </button>
        </div>
      </div>

      <div className={styles.userDetails}>
        <div>
          <p className={styles.detailLabel}>Last Login</p>
          <p className={styles.detailValue}>{formatDate(user.lastLogin)}</p>
        </div>
        <div>
          <p className={styles.detailLabel}>Created</p>
          <p className={styles.detailValue}>{formatDate(user.createdAt)}</p>
        </div>
      </div>

      <div className={styles.userFooter}>
        <button
          type="button"
          className={styles.statusToggle}
          onClick={handleStatusAction}
          disabled={isUpdating}
        >
          {isUpdating ? 'Updating…' : user.status === 'active' ? 'Suspend' : 'Reactivate'}
        </button>
        {user.vendorId && <span className={styles.vendorTag}>Vendor: {user.vendorId}</span>}
      </div>
    </div>
  );
}

function UserCardSkeleton() {
  return (
    <div className={styles.skeletonCard} aria-hidden="true">
      <div className={`${styles.skeletonBlock} ${styles.skeletonAvatar}`} />
      <div className={styles.skeletonBody}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonTitle}`} />
        <div className={`${styles.skeletonBlock} ${styles.skeletonLine}`} />
        <div className={`${styles.skeletonBlock} ${styles.skeletonBadge}`} />
      </div>
    </div>
  );
}

const UsersManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterRole, setFilterRole] = useState<'all' | User['role']>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | User['status']>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Notify LoginModal of user changes (LoginModal may subscribe in future)
  const notifyLoginModal = () => {
    window.dispatchEvent(new CustomEvent('usersUpdated'));
  };

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getAll();
      setUsers(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load users';
      setError(message);
      toastService.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const stats = useMemo(() => {
    const activeCount = users.filter((u) => u.status === 'active').length;
    const suspendedCount = users.filter((u) => u.status === 'suspended').length;
    const adminCount = users.filter((u) => u.role === 'admin' || u.role === 'super-admin').length;
    const superAdminCount = users.filter((u) => u.role === 'super-admin').length;
    const vendorLinkedCount = users.filter((u) => !!u.vendorId).length;
    return { activeCount, suspendedCount, adminCount, superAdminCount, vendorLinkedCount };
  }, [users]);

  const filteredAndSortedUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return users
      .filter((user) => {
        const matchesSearch =
          user.name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term);
        const matchesRole = filterRole === 'all' || user.role === filterRole;
        const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
        return matchesSearch && matchesRole && matchesStatus;
      })
      .sort((a, b) => {
        const aValue = getSortKey(a, sortBy);
        const bValue = getSortKey(b, sortBy);
        if (aValue === bValue) return 0;
        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        }
        return aValue < bValue ? 1 : -1;
      });
  }, [users, searchTerm, filterRole, filterStatus, sortBy, sortOrder]);

  const handleCreateUser = async (userData: Omit<User, '_id' | 'id'>) => {
    try {
      setFormSaving(true);
      const newUser = await userService.create(userData);
      setUsers((prev) => [newUser, ...prev]);
      setShowCreateModal(false);
      notifyLoginModal();
      toastService.success('User created');
    } catch (err: unknown) {
      toastService.error(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setFormSaving(false);
    }
  };

  const handleUpdateUser = async (updatedUser: User) => {
    const id = getUserId(updatedUser);
    try {
      setFormSaving(true);
      const saved = await userService.update(id, updatedUser);
      setUsers((prev) => prev.map((u) => getUserId(u) === id ? saved : u));
      setEditingUser(null);
      notifyLoginModal();
      toastService.success('User updated');
    } catch (err: unknown) {
      toastService.error(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!pendingDelete) return;
    try {
      setDeleteLoading(true);
      await userService.delete(pendingDelete);
      setUsers((prev) => prev.filter((u) => getUserId(u) !== pendingDelete));
      setPendingDelete(null);
      notifyLoginModal();
      toastService.success('User deleted');
    } catch (err: unknown) {
      toastService.error(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleStatus = async (user: User, nextStatus: User['status']) => {
    const id = getUserId(user);
    try {
      setStatusUpdatingId(id);
      const saved = await userService.update(id, { status: nextStatus });
      setUsers((prev) => prev.map((u) => (getUserId(u) === id ? saved : u)));
      notifyLoginModal();
      toastService.success(nextStatus === 'active' ? 'User reactivated' : 'User suspended');
    } catch (err: unknown) {
      toastService.error(err instanceof Error ? err.message : 'Failed to update user status');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const hasActiveFilters = searchTerm.trim() !== '' || filterRole !== 'all' || filterStatus !== 'all';
  const showEmptyFiltered = !loading && !error && filteredAndSortedUsers.length === 0 && hasActiveFilters;
  const showEmptyNoUsers = !loading && !error && users.length === 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Users Management</h1>
        <p className={styles.subtitle}>Manage user accounts, roles, and access permissions</p>
      </div>

      <div className={styles.controlsBar}>
        <div className={styles.searchWrap}>
          <SearchIcon size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.controlsGroup}>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value as typeof filterRole)}
            className={styles.filterSelect}
            aria-label="Filter by role"
          >
            <option value="all">All Roles</option>
            {PERMISSION_LEVELS.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
            className={styles.filterSelect}
            aria-label="Filter by status"
          >
            <option value="all">All Statuses</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-') as [SortField, 'asc' | 'desc'];
              setSortBy(field);
              setSortOrder(order);
            }}
            className={styles.sortSelect}
            aria-label="Sort users"
          >
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="email-asc">Email A-Z</option>
            <option value="role-asc">Role A-Z</option>
            <option value="lastLogin-desc">Recent Login</option>
            <option value="createdAt-desc">Newest First</option>
          </select>

          <button type="button" onClick={() => setShowCreateModal(true)} className={styles.createButton}>
            <PlusIcon size={20} />
            Create User
          </button>
        </div>
      </div>

      <div className={styles.statTileRow}>
        <StatTile icon={<UsersIcon size={16} />} label="Total Users" value={users.length} loading={loading} />
        <StatTile
          icon={<CheckIcon size={16} />}
          label="Active"
          value={stats.activeCount}
          subtitle={stats.suspendedCount > 0 ? `${stats.suspendedCount} suspended` : undefined}
          trendTone="success"
          loading={loading}
        />
        <StatTile
          icon={<UserIcon size={16} />}
          label="Administrators"
          value={stats.adminCount}
          subtitle={stats.superAdminCount > 0 ? `${stats.superAdminCount} super-admin` : undefined}
          loading={loading}
        />
        <StatTile
          icon={<VendorsIcon size={16} />}
          label="Vendor-linked"
          value={stats.vendorLinkedCount}
          loading={loading}
        />
      </div>

      {loading ? (
        <div className={styles.skeletonGrid}>
          {Array.from({ length: 6 }).map((_, index) => (
            <UserCardSkeleton key={index} />
          ))}
        </div>
      ) : error ? (
        <div className={styles.emptyState}>
          <p className={styles.errorText}>{error}</p>
          <button type="button" className={styles.retryButton} onClick={loadUsers}>
            Try again
          </button>
        </div>
      ) : showEmptyNoUsers ? (
        <div className={styles.emptyState}>
          <UsersIcon size={40} className={styles.emptyStateIcon} />
          <h3 className={styles.emptyStateTitle}>No users yet</h3>
          <p className={styles.emptyStateDescription}>Create your first user to get started.</p>
          <button type="button" className={styles.createButton} onClick={() => setShowCreateModal(true)}>
            <PlusIcon size={18} />
            Create User
          </button>
        </div>
      ) : showEmptyFiltered ? (
        <div className={styles.emptyState}>
          <SearchIcon size={40} className={styles.emptyStateIcon} />
          <h3 className={styles.emptyStateTitle}>No users found</h3>
          <p className={styles.emptyStateDescription}>Try adjusting your search or filter criteria.</p>
        </div>
      ) : (
        <div className={styles.usersGrid}>
          {filteredAndSortedUsers.map((user) => (
            <UserCard
              key={getUserId(user)}
              user={user}
              statusUpdatingId={statusUpdatingId}
              onEdit={setEditingUser}
              onDelete={setPendingDelete}
              onToggleStatus={handleToggleStatus}
            />
          ))}
        </div>
      )}

      {(showCreateModal || editingUser) && (
        <UserFormModal
          user={editingUser}
          saving={formSaving}
          onSave={editingUser ? handleUpdateUser : handleCreateUser}
          onClose={() => {
            setShowCreateModal(false);
            setEditingUser(null);
          }}
        />
      )}

      <Modal
        isOpen={!!pendingDelete}
        onClose={() => !deleteLoading && setPendingDelete(null)}
        title="Delete User"
        size="sm"
        footer={
          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={() => setPendingDelete(null)}
              className={styles.modalSecondary}
              disabled={deleteLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteUser}
              className={styles.modalDanger}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        }
      >
        <p className={styles.confirmMessage}>
          Are you sure you want to delete this user? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default UsersManagement;
